import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, ChevronRight, Plus, X, Edit, Calendar as CalendarIcon, Clock } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const COLORS = ['#3b82f6', '#8b5cf6', '#ef4444', '#f59e0b', '#10b981', '#ec4899', '#06b6d4'];
const VIEWS = ['month', 'week'];
const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startDay = firstDay.getDay() - 1;
  if (startDay < 0) startDay = 6;
  const days = [];
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
  }
  return days;
}

function getWeekDays(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const wd = new Date(monday);
    wd.setDate(monday.getDate() + i);
    days.push(wd);
  }
  return days;
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', startDate: '', endDate: '', color: '#3b82f6', ticketId: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStaff(); }, []);
  useEffect(() => { loadEvents(); }, [currentDate, view, selectedStaff]);

  const loadStaff = async () => {
    try {
      const res = await api.get('/users/staff');
      setStaffList(res.data);
    } catch (err) {}
  };

  const loadEvents = async () => {
    try {
      let start, end;
      if (view === 'month') {
        start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else {
        const weekDays = getWeekDays(currentDate);
        start = weekDays[0];
        end = weekDays[6];
      }
      const params = { start: start.toISOString(), end: end.toISOString() };
      if (selectedStaff) params.userId = selectedStaff;
      const res = await api.get('/calendar', { params });
      setEvents(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'month') {
      d.setMonth(d.getMonth() + dir);
    } else {
      d.setDate(d.getDate() + (dir * 7));
    }
    setCurrentDate(d);
  };

  const toLocalDatetime = (d) => {
    const date = new Date(d);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const openCreate = (date) => {
    const d = date || new Date();
    const start = new Date(d); start.setHours(9, 0, 0, 0);
    const end = new Date(d); end.setHours(10, 0, 0, 0);
    setForm({
      title: '', description: '',
      startDate: toLocalDatetime(start),
      endDate: toLocalDatetime(end),
      color: '#3b82f6', ticketId: ''
    });
    setEditingEvent(null);
    setShowForm(true);
  };

  const openEdit = (event) => {
    setForm({
      title: event.Title,
      description: event.Description || '',
      startDate: toLocalDatetime(event.StartDate),
      endDate: toLocalDatetime(event.EndDate),
      color: event.Color || '#3b82f6',
      ticketId: event.TicketId || ''
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await api.put(`/calendar/${editingEvent.Id}`, form);
        toast.success('Etkinlik güncellendi');
      } else {
        await api.post('/calendar', form);
        toast.success('Etkinlik oluşturuldu');
      }
      setShowForm(false);
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    }
  };


  const getEventsForDay = (date) => {
    return events.filter(e => {
      const start = new Date(e.StartDate);
      const end = new Date(e.EndDate);
      return date >= new Date(start.toDateString()) && date <= new Date(end.toDateString());
    });
  };

  const today = new Date();
  const monthDays = view === 'month' ? getMonthDays(currentDate.getFullYear(), currentDate.getMonth()) : [];
  const weekDays = view === 'week' ? getWeekDays(currentDate) : [];

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-800">Takvim</h1>
          <div className="flex bg-white rounded-lg border border-slate-200 overflow-hidden">
            {VIEWS.map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-medium transition-all ${view === v ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                {v === 'month' ? 'Ay' : 'Hafta'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Tüm Personel</option>
            {staffList.map(s => <option key={s.Id} value={s.Id}>{s.FullName}</option>)}
          </select>
          <button onClick={() => openCreate()} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <Plus size={18} /> Etkinlik Ekle
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-slate-100 px-5 py-3">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronLeft size={20} /></button>
        <div className="text-center">
          <h2 className="text-lg font-semibold text-slate-800">
            {view === 'month'
              ? `${MONTHS_TR[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              : `${weekDays[0]?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - ${weekDays[6]?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`
            }
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100">Bugün</button>
          <button onClick={() => navigate(1)} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronRight size={20} /></button>
        </div>
      </div>

      {/* Month View */}
      {view === 'month' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-100">
            {DAYS_TR.map(d => (
              <div key={d} className="px-2 py-2.5 text-center text-xs font-semibold text-slate-500 bg-slate-50">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map((day, i) => {
              const dayEvents = getEventsForDay(day.date);
              const isToday = isSameDay(day.date, today);
              return (
                <div key={i} onClick={() => openCreate(day.date)}
                  className={`min-h-[100px] border-b border-r border-slate-100 p-1.5 cursor-pointer hover:bg-blue-50/30 transition-colors ${!day.isCurrentMonth ? 'bg-slate-50/50' : ''}`}>
                  <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-blue-600 text-white' : day.isCurrentMonth ? 'text-slate-700' : 'text-slate-300'
                  }`}>
                    {day.date.getDate()}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map(ev => (
                      <div key={ev.Id} onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                        className="text-[10px] px-1.5 py-0.5 rounded truncate text-white font-medium cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: ev.Color || '#3b82f6' }}
                        title={`${ev.Title} (${ev.UserName})`}>
                        {ev.Title} • {ev.UserName?.split(' ')[0]}
                      </div>
                    ))}
                    {dayEvents.length > 3 && <p className="text-[10px] text-slate-400 px-1">+{dayEvents.length - 3} daha</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-100">
            {weekDays.map((d, i) => {
              const isToday = isSameDay(d, today);
              return (
                <div key={i} className={`px-2 py-3 text-center border-r border-slate-100 ${isToday ? 'bg-blue-50' : 'bg-slate-50'}`}>
                  <p className="text-xs font-medium text-slate-500">{DAYS_TR[i]}</p>
                  <p className={`text-lg font-bold ${isToday ? 'text-blue-600' : 'text-slate-800'}`}>{d.getDate()}</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 min-h-[400px]">
            {weekDays.map((d, i) => {
              const dayEvents = getEventsForDay(d);
              return (
                <div key={i} onClick={() => openCreate(d)} className="border-r border-slate-100 p-2 cursor-pointer hover:bg-blue-50/30 transition-colors">
                  <div className="space-y-1.5">
                    {dayEvents.map(ev => (
                      <div key={ev.Id} onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                        className="p-2 rounded-lg text-white cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: ev.Color || '#3b82f6' }}>
                        <p className="text-xs font-semibold truncate">{ev.Title}</p>
                        <p className="text-[10px] opacity-80 flex items-center gap-1 mt-0.5">
                          <Clock size={10} />
                          {new Date(ev.StartDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-[10px] opacity-70 mt-0.5">{ev.UserName}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">{editingEvent ? 'Etkinlik Düzenle' : 'Yeni Etkinlik'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Başlık *</label>
                <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Açıklama</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Başlangıç *</label>
                  <input type="datetime-local" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})} required
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bitiş *</label>
                  <input type="datetime-local" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})} required
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Renk</label>
                <div className="flex gap-2">
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setForm({...form, color: c})}
                      className={`w-8 h-8 rounded-full transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">
                  {editingEvent ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
